import { formatPtoGrantedDate } from '@/helpers/formatPtoGrantedDate';
import { getMostRecentConditionallyApprovedHistoryItem } from '@/helpers/getMostRecentConditionallyApprovedHistoryItem';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import { validateDate } from '@/helpers/validateDate';
import { useConditionallyApprovedInstallFlags } from '@/hooks/useConditionallyApprovedInstallFlags';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { Alert, Box, FormikTextInput, TextLink, toast } from '@palmetto/palmetto-components';
import { Field, FieldArray, Formik, FormikValues } from 'formik';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import {
  ActivationDocumentTypes,
  ActivationPackagePhotoCategory,
  DeepPartial,
  DocumentTypes,
  EquipmentType,
  EquipmentTypeLabels,
  Flag,
  FlagPurposeType,
  ActivationPackage as IActivationPackage,
  MilestoneType,
  ProgramType,
  QueueHistory,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
  RequirementFlagSettingsType,
  UserPermissions,
  validateMonitoringIdFormat,
  VerifyExistingSystemDocumentTypes,
} from 'types';

import { DATE_FORMAT } from '../../helpers/dates';
import { useGetAccountQuery } from '../../services/accounts';
import {
  useSaveActivationEnergyBackupPackageMutation,
  useSubmitActivationEnergyBackupPackageMutation,
} from '../../services/activationPackage';
import { isErrorWithData } from '../../services/helpers';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { ActivationPackageActions } from '../../types/ActivationPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isInternalUser } from '../auth/RequireIsInternalUser';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { ApprovedInterconnectionAgreement } from '../MilestonePackagePartials/ApprovedInterconnectionAgreement';
import { DocumentList } from '../MilestonePackagePartials/Documents/DocumentList';
import { PhotosList } from '../MilestonePackagePartials/Documents/PhotosList';
import { Footer } from '../MilestonePackagePartials/Footer';
import { Incentives } from '../MilestonePackagePartials/Incentives';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { UtilityBill } from '../MilestonePackagePartials/UtilityBill';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { FlagBox } from '../QueueFlags/FlagBox';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

import { ConditionallyApprovedDocs } from './ConditionallyApprovedDocs';
import { ProofOfPto } from './ProofOfPto';

export const formatActivationPackageData = (values: FormikValues): DeepPartial<IActivationPackage> => {
  return {
    monitoring: {
      batteryMonitoringSiteId: values.batteryMonitoringSiteId,
    },
    ptoGrantedDate: formatPtoGrantedDate(values),
    utility: {
      tariffId: values.tariffId === '' ? null : values.tariffId,
    },
  };
};
const activationPackageSchema = yup.object().shape({
  batteryMonitoringSiteId: yup.string().required(),
});

const getMostRecentObj = (array: any[]) => {
  return array?.reduce((prev: any, current: any) => {
    return prev.date > current.date ? prev : current;
  });
};

export const ActivationEnergyBackupPackage = ({
  canSubmitActivationPackage,
}: {
  canSubmitActivationPackage: boolean;
}) => {
  const navigate = useNavigate();
  const { id = '', action = ActivationPackageActions.review } = useParams<{
    id: string;
    action: ActivationPackageActions;
  }>();
  const allowsAction = useIsOrgReadOnly();

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const { data: queuesData, isLoading: isQueueLoading } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.activationPackage, QueueType.installPackage, QueueType.permissionToOperate],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.activationPackage);
  const installationQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);
  const ptoQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.permissionToOperate);
  const [triggerSave] = useSaveActivationEnergyBackupPackageMutation();
  const [triggerSubmit] = useSubmitActivationEnergyBackupPackageMutation();
  const { claims } = useAuth();
  const permissions = usePermissions();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const triggerSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.activation);
  const showCheckboxes = isInternalUser(claims, true);
  const canSubmitReview = action === ActivationPackageActions.review && queue;
  const installConditionallyApprovedHistoryItems = installationQueue?.history?.filter(
    (item: QueueHistory) => item.changedTo === QueueStatus.conditionallyApproved,
  );
  const mostRecentInstallConditionallyApprovedHistoryItem = getMostRecentConditionallyApprovedHistoryItem(
    installConditionallyApprovedHistoryItems,
  );
  const mostRecentInstallConditionallyApprovedFlags =
    mostRecentInstallConditionallyApprovedHistoryItem?.openFlags ?? [];
  const conditionallyApprovalDate = mostRecentInstallConditionallyApprovedHistoryItem?.date;
  const conditionallyApprovedInstallFlags = useConditionallyApprovedInstallFlags({
    installationQueueFlags: installationQueue?.flags,
    mostRecentInstallConditionallyApprovedFlags,
    conditionallyApprovalDate,
  });

  const stipulationFlagsPendingToBeReviewed = conditionallyApprovedInstallFlags?.filter(
    (flag: Flag) => flag.purposeType === FlagPurposeType.stipulation && !flag.resolved,
  );

  const isLoading = isQueueLoading || isAccountLoading;

  const installNote = useMemo(() => {
    if (!account) {
      return;
    }
    if (
      installationQueue &&
      [QueueStatus.approved, QueueStatus.conditionallyApproved].includes(installationQueue.status)
    ) {
      return getMostRecentObj(installationQueue.approvalNotes)?.note;
    }
    return account?.notes?.milestones?.install;
  }, [account, installationQueue]);

  const subQueueType = QueueType.permissionToOperate;
  const subQueue = ptoQueue;
  const subQueueNeedsApproval = subQueue
    ? ![QueueStatus.approved, QueueStatus.rejected, QueueStatus.completeHoldForApproval].includes(subQueue.status)
    : false;
  const subQueueInfo = subQueue && subQueueType ? { type: subQueueType, status: subQueue.status } : undefined;
  const showConditionallyApprovedDocs =
    installationQueue?.status === QueueStatus.conditionallyApproved &&
    mostRecentInstallConditionallyApprovedFlags?.length > 0;
  const checkboxCategories = [
    ActivationDocumentTypes.ptoConfirmation,
    ActivationDocumentTypes.incentives,
    DocumentTypes.utilityBill,
    ActivationPackagePhotoCategory.siteImprovements,
  ];
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });

  if (
    showConditionallyApprovedDocs &&
    checkboxCategories.indexOf(ActivationDocumentTypes.conditionalApproval) === -1
  ) {
    checkboxCategories.push(ActivationDocumentTypes.conditionalApproval);
  }

  const ptoIsLocked = isFinCoUser
    ? false
    : [
        QueueStatus.approved,
        QueueStatus.completeHoldForApproval,
        QueueStatus.submitted,
        QueueStatus.resubmitted,
      ].includes(ptoQueue?.status);
  const initialValues = {
    batteryMonitoringSiteId: account?.batteryMonitoringSiteId,
    ptoGrantedDate: account?.ptoGrantedDate ? DateTime.fromISO(account?.ptoGrantedDate).toFormat(DATE_FORMAT) : '',
    lseId: account?.utility?.lseId,
    tariffId: account?.utility?.tariffId || '',
    state: account?.address?.state,
    systemDesign: account?.systemDesign || { equipment: [] },
  };

  useEffect(() => {
    if (!action || !ActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (action === ActivationPackageActions.review && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/activation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/activation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue]);

  const handleSave = async ({ accountId, data }: { accountId: string; data: FormikValues }) => {
    const formattedData = formatActivationPackageData(data);
    try {
      await triggerSave({ accountId, savePackage: formattedData }).unwrap();
      triggerSuccessMessage();
    } catch (e) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage);
      } else {
        throw new Error('Error saving Activation Package');
      }
    }
  };
  const handleSubmit = async (values: FormikValues, { setSubmitting }: any) => {
    try {
      const formattedData = formatActivationPackageData(values) as IActivationPackage;
      await triggerSubmit({ accountId: id, activationPackage: formattedData }).unwrap();
      toast.success('Activation Package Submitted');
      navigate(`/accounts/${id}`);
    } catch (e: any) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(errorMessage);
      } else {
        toast.error('Error saving Activation Package');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;
    const batteryManufacturer = values.systemDesign?.equipment?.[0]?.manufacturer;
    const batteryMonitoringIdFormat = validateMonitoringIdFormat(
      values.batteryMonitoringSiteId,
      batteryManufacturer,
    );
    if (values.batteryMonitoringSiteId && !batteryMonitoringIdFormat.valid) {
      errors.batteryMonitoringSiteId = `Monitoring Site ID must match the required format: ${batteryMonitoringIdFormat.expectedFormat}.`;
    }
    if (!values.ptoGrantedDate) {
      errors.ptoGrantedDate = 'PTO Granted Date is required';
    } else if (!validateDate(values.ptoGrantedDate)) {
      errors.ptoGrantedDate = 'PTO Granted Date is invalid. Must be in the format MM-DD-YYYY';
    }
    return errors;
  };

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.activationPackage} />
      <Helmet>
        <title>{`${account?.primaryApplicantName} Activation Package`}</title>
      </Helmet>
      <MainContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmit}
          enableReinitialize={true}
          validateOnChange={false}
          validate={handleValidation}
          validationSchema={activationPackageSchema}
        >
          <EnhancedFormikForm>
            <ActionsList
              milestoneType={MilestoneType.activation}
              accountId={queue?.account?.id}
              canSubmitPackage={allowsAction && canSubmitActivationPackage}
              disableApproval={subQueueNeedsApproval}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview}
              onSave={handleSave}
              disabled={!allowsAction || !canEditPackage}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              conditionallyApprovedFlags={stipulationFlagsPendingToBeReviewed}
              queue={queue}
              subQueueInfo={subQueueInfo}
            />
            {queue?.status === QueueStatus.rejected || isFinCoUser ? (
              <QueueBanner
                setIsNotesOpen={setIsNotesOpen}
                queueType={QueueType.activationPackage}
                isFinCoUser={isFinCoUser}
                is8090Project={Boolean(queue?.aiPackageReviewStatus)}
              />
            ) : null}
            {showUnderReviewAlert ? (
              <Alert
                variant="warning"
                title="Activation Package In Review Queue"
                message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                hasIcon
              />
            ) : null}
            {action === ActivationPackageActions.review && !canSubmitReview ? (
              <Alert
                variant="warning"
                title="Activation Package Not Submitted"
                message="This activation package is not ready to be reviewed yet. It has not been submitted."
                hasIcon
              />
            ) : null}
            {queue?.status !== QueueStatus.rejected && installNote && (
              <Alert variant="info" hasIcon title="Install Notes" message={installNote} />
            )}
            {showConditionallyApprovedDocs && (
              <ConditionallyApprovedDocs
                flaggedDocs={conditionallyApprovedInstallFlags}
                queue={queue}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
              />
            )}
            <FormSection title="Existing System">
              {!account?.existingSystemDetails ? (
                <Box>No existing system details provided.</Box>
              ) : (
                <Box direction="column" gap="sm">
                  <Box direction="row" gap="xs">
                    <strong>Has Existing System:</strong>{' '}
                    {account?.existingSystemDetails?.hasExistingSystem ? 'Yes' : 'No'}
                  </Box>
                  {account?.existingSystemDetails?.hasExistingSystem && (
                    <>
                      <Box direction="row" gap="xs">
                        <strong>AC System Size (kW):</strong> {account?.existingSystemDetails?.acSystemSize || '--'}
                      </Box>
                      <Box direction="row" gap="xs">
                        <strong>Finance Type:</strong>{' '}
                        {account?.existingSystemDetails?.financeType?.toUpperCase() || '--'}
                      </Box>
                      <DocumentList
                        title="Existing Solar System Documents"
                        documentTypeFilter={[VerifyExistingSystemDocumentTypes.existingSystemPhotos]}
                        allowArchive={false}
                        showUploadButton={false}
                        showTypeLabelOnItems={false}
                        isPackageEditable={canEditPackage}
                      />
                      <FlagBox
                        dataKey="Energy Backup Existing Solar System"
                        flagType={RequirementFlagSettingsType.existingSystemEnergyBackup}
                      />
                    </>
                  )}
                </Box>
              )}
            </FormSection>
            <FieldArray name="systemDesign.equipment">
              {({ form }) => (
                <FormSection title={'System Details'}>
                  {form.values.systemDesign.equipment && form.values.systemDesign.equipment.length > 0 ? (
                    form.values.systemDesign.equipment.map((item: any, index: number) => (
                      <Box key={index} gap="sm" direction="row">
                        <Box gap="xs" width="3xl">
                          <Box direction="row" justifyContent="space-between" alignItems="center">
                            <strong>{EquipmentTypeLabels[item.type as EquipmentType]}</strong>
                          </Box>
                          <Box>
                            {item.manufacturer} {item.models[0]?.name}
                          </Box>
                          <Box direction="column" gap="sm">
                            {item.models[0]?.modelNumber && (
                              <Box direction="row" gap="xs">
                                {item.models[0]?.modelNumber}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Box>No equipment added.</Box>
                  )}
                  <FlagBox
                    dataKey="System Design Issue"
                    flagType={RequirementFlagSettingsType.systemDesignIssueEnergyBackup}
                  />
                </FormSection>
              )}
            </FieldArray>
            <FormSection
              title="Monitoring & System Settings"
              description={
                <Box childGap="xs" color="body-secondary">
                  <p>
                    <b>Required:</b> Monitoring portal ID for the battery.
                  </p>
                  <p>Ensure site access is shared to Palmetto as owner.</p>
                  <p>
                    <TextLink
                      href="https://help.palmetto.finance/en/articles/8306383-monitoring-portal-access"
                      target="_blank"
                      rel="noreferrer"
                    >
                      See monitoring portal access requirements
                    </TextLink>
                  </p>
                </Box>
              }
            >
              <FlagBox
                dataKey="Battery Monitoring ID"
                flagType={RequirementFlagSettingsType.batteryMonitoringId}
                displayWithNoQueue
              >
                <Box
                  direction={{ base: 'column', tablet: 'row' }}
                  alignItems="center"
                  childGap={{ base: 'lg', tablet: '3xl' }}
                  width="100"
                >
                  <Field
                    type="text"
                    name="batteryMonitoringSiteId"
                    id="batteryMonitoringSiteId"
                    hideLabel
                    isRequired
                    component={FormikTextInput}
                    isDisabled={!canEditPackage}
                  />
                </Box>
              </FlagBox>
              <Box alignItems="flex-end">
                <Box width={{ base: '100', tablet: '4xl' }} gap="md" margin={'md 0 0 0'}>
                  <FlagBox
                    dataKey="Access to LightReach - Battery"
                    flagType={RequirementFlagSettingsType.accessToLightReachBattery}
                  />
                  <FlagBox
                    dataKey="Configuration and Settings - Battery"
                    flagType={RequirementFlagSettingsType.configurationSettingsBattery}
                  />
                </Box>
              </Box>
            </FormSection>
            <ProofOfPto
              disabled={ptoIsLocked}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isReviewMode={action === ActivationPackageActions.review}
              isPackageEditable={canEditPackage}
            />
            <UtilityBill
              account={account}
              originContext={QueueType.activationPackage}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <ApprovedInterconnectionAgreement
              account={account}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <Incentives
              originContext={QueueType.activationPackage}
              showCheckboxes={showCheckboxes}
              checkboxCategories={checkboxCategories}
              isPackageEditable={canEditPackage}
            />
            <FormSection title="Site Improvement" description="if applicable, photos of the site improvements made.">
              <PhotosList
                title="Site Improvement Photos"
                category={ActivationPackagePhotoCategory.siteImprovements}
                baseCategory="Activation Photo Documentation"
                flagType={RequirementFlagSettingsType.siteImprovement}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                programType={ProgramType.energyBackup}
                isPackageEditable={canEditPackage}
              />
            </FormSection>
            <Footer
              canSubmitPackage={allowsAction && canSubmitActivationPackage}
              disableApproval={subQueueNeedsApproval}
              isLoading={isLoading}
              isFirstSubmission={!queue}
              canSubmitReview={canSubmitReview}
              onSave={handleSave}
              disabled={!allowsAction || !canEditPackage}
              conditionallyApprovedFlags={stipulationFlagsPendingToBeReviewed}
              subQueueInfo={subQueueInfo}
            />
          </EnhancedFormikForm>
        </Formik>
      </MainContainer>
    </QueueFlagsProvider>
  );
};
